import { Sparkles } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { useWhatsNew } from '@/components/whats-new/whats-new-provider';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { isNavItemActive, whatsNewHref } from '@/lib/navigation';
import { cn } from '@/lib/utils';

/**
 * Whether the newest edition of the catalog is still unseen (backend and
 * this tab): what the dots, "1 nova" and the teasers follow (NEWS-45…50).
 */
export function useHasUnseenEdition(): boolean {
  const { latest, isSeen } = useWhatsNew();
  return latest !== null && !isSeen(latest.id);
}

/** The pulsing blue dot (still under reduced motion). */
export function NewsDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-testid="whats-new-dot"
      className={cn('relative inline-flex size-[7px] shrink-0', className)}
    >
      <span className="absolute inset-0 rounded-full bg-[#155DFC] motion-safe:animate-ping motion-safe:opacity-60" />
      <span className="relative size-full rounded-full bg-[#155DFC]" />
    </span>
  );
}

const SHEET_ROW =
  'flex min-h-touch w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring';

/**
 * "Novidades" (AD-046): in the sidebar footer (NEWS-45) and the Mais sheet
 * (NEWS-48), with the dot and "1 nova" while the newest edition is unseen;
 * in the collapsed rail only the dot, on the icon.
 */
export function WhatsNewNavItem({
  variant,
  onNavigate,
}: {
  variant: 'sidebar' | 'sheet';
  /** E.g. closes the Mais sheet. */
  onNavigate?: () => void;
}) {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const pathname = useLocation().pathname;
  const unseen = useHasUnseenEdition();
  const href = whatsNewHref(tenant.slug);
  const label = LL.whatsNew.nav.label();
  const badge = unseen && (
    <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold text-[#155DFC] group-data-[collapsible=icon]:hidden">
      <NewsDot />
      {LL.whatsNew.nav.newBadge()}
    </span>
  );

  if (variant === 'sheet') {
    return (
      <Link to={href} onClick={onNavigate} className={SHEET_ROW}>
        <Sparkles aria-hidden="true" className="size-5 text-muted-foreground" />
        <span>{label}</span>
        {badge}
      </Link>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isNavItemActive(href, pathname)}
        tooltip={label}
        className="relative"
      >
        <Link to={href} onClick={onNavigate}>
          <Sparkles aria-hidden="true" />
          <span>{label}</span>
          {badge}
          {unseen && (
            <NewsDot className="absolute right-1 top-1 hidden group-data-[collapsible=icon]:inline-flex" />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
